// Tipo do card (formato e assunto Schema.org) e ação (ler, assistir...) com "feito": pílulas numa fileira própria acima
// das etiquetas, como o tipo aparece na referência. Troca manual de tipo grava kindOrigin='user' e a reanálise não
// desfaz (§4.2). Os menus são popover no computador e folha modal no celular (MenuSheet).
import type { JSX } from "preact";
import type { Card, CardAction, CardKind } from "../../../../shared/types.ts";
import { IconCheck, IconChevronDown, IconImage, IconLink, IconNote, IconQuote, IconSwatch } from "../Icons.tsx";
import { entityLabel, KIND_LABEL, kindChoices, quietIngest } from "../lib.ts";
import { failed, store, toast, waitsForAi } from "../state.ts";
import { closeMenu, MenuSheet } from "./Extras.tsx";
import { FACE_ICON } from "./Media.tsx";

/** Assuntos oferecidos no menu (os rótulos de todos os que a ingestão grava ficam em ENTITY_LABEL). */
const ENTITIES = ["Article", "Book", "Movie", "Product", "Recipe", "Repository", "SocialMediaPosting", "Photograph", "Place", "Event", "MusicRecording", "Course"];
const ACTIONS: [CardAction, string][] = [["ler", "ler"], ["assistir", "assistir"], ["ouvir", "ouvir"], ["comprar", "comprar"], ["fazer", "fazer"]];

const cap = (s: string) => (s === "pdf" ? "PDF" : s.charAt(0).toUpperCase() + s.slice(1));
export const kindName = (c: Pick<Card, "kind" | "entityType">) => (c.entityType ? entityLabel(c.entityType) : cap(KIND_LABEL[c.kind]));
/** Ícone do tipo: os mesmos das faces de PDF, vídeo e áudio na grade; nota e destaque ficam com a folha. */
const KIND_ICON: Partial<Record<CardKind, () => JSX.Element>> = { ...FACE_ICON, image: IconImage, link: IconLink, color: IconSwatch, quote: IconQuote };

const Opt = ({ on, label, run }: { on: boolean; label: string; run: () => void }) => (
  <button type="button" class="rd-opt" aria-pressed={on} onClick={run}>
    {label}
  </button>
);

/** Nota do menu: de onde veio o tipo. Sem a chave da IA a escolha foi da página (JSON-LD) ou do formato, não da análise. */
const kindNote = (c: Card) =>
  c.kindOrigin === "user" ? "Escolha sua: uma nova análise não muda." : quietIngest(c) ? "Escolhido automaticamente. Se você trocar, fica a sua escolha." : "Escolhido pela análise. Se você trocar, fica a sua escolha.";

/** Pílula do tipo + menu. */
export function KindChip({ card: c }: { card: Card }) {
  const id = `kind-${c.id}`;
  const Icon = KIND_ICON[c.kind] ?? IconNote;
  const set = (fields: Partial<Pick<Card, "kind" | "entityType">>) => store.update(c.id, fields).catch(failed);
  const label = kindName(c);
  const kinds = kindChoices(c);
  return (
    <MenuSheet
      id={id}
      label="Tipo do card"
      trigger={(p) => (
        <button type="button" class="rd-chip" {...p} aria-label={`Tipo: ${label}`} title="Trocar o tipo">
          <Icon />
          {label}
          <IconChevronDown />
        </button>
      )}
    >
      {/* Um formato só (imagem, PDF...): não há o que trocar, fica só o assunto. */}
      {kinds.length > 1 && (
        <>
          <p class="menu-label">Formato</p>
          <div class="rd-opts">
            {kinds.map((k) => (
              <Opt key={k} on={c.kind === k} label={cap(KIND_LABEL[k])} run={() => c.kind !== k && set({ kind: k })} />
            ))}
          </div>
        </>
      )}
      <p class="menu-label">Assunto</p>
      <div class="rd-opts">
        <Opt on={!c.entityType} label="Nenhum" run={() => c.entityType && set({ entityType: null })} />
        {ENTITIES.map((k) => (
          <Opt key={k} on={c.entityType === k} label={entityLabel(k)} run={() => c.entityType !== k && set({ entityType: k })} />
        ))}
        {c.entityType && !ENTITIES.includes(c.entityType) && <Opt on label={entityLabel(c.entityType)} run={() => {}} />}
      </div>
      <p class="rd-menu-note">{kindNote(c)}</p>
      {/* Sem a chave da IA, analisar de novo só repetiria a mesma análise (e buscaria a página outra vez). */}
      {!waitsForAi(c) && (
        <button
          type="button"
          class="text-btn rd-menu-redo"
          onClick={() => {
            closeMenu(id);
            store.reprocess(c.id).then(() => toast({ key: `redo:${c.id}`, text: "Nova análise pedida. Etiquetas que você tirou e o tipo escolhido ficam." }), () => {});
          }}
        >
          Analisar de novo
        </button>
      )}
    </MenuSheet>
  );
}

/** Ação do card (tarefa = card com ação; feito:sim / feito:nao na busca). */
export function ActionChip({ card: c }: { card: Card }) {
  const label = c.action ? `Para ${c.action}` : "Marcar para…";
  return (
    <MenuSheet
      id={`action-${c.id}`}
      label="Ação do card"
      trigger={(p) => (
        <button type="button" class="rd-chip" {...p} aria-label={`Ação: ${label}`} title="O que fazer com este card">
          {label}
          <IconChevronDown />
        </button>
      )}
    >
      <p class="menu-label">Guardei para</p>
      <div class="rd-opts">
        {ACTIONS.map(([a, l]) => (
          <Opt key={a} on={c.action === a} label={cap(l)} run={() => c.action !== a && store.setAction(c.id, a).catch(failed)} />
        ))}
        <Opt on={!c.action} label="Nada" run={() => c.action && store.setAction(c.id, null).catch(failed)} />
      </div>
    </MenuSheet>
  );
}

/** "Feito" da ação (só aparece quando há ação). */
export const DoneChip = ({ card: c }: { card: Card }) => (
  <button type="button" class="rd-chip rd-done" aria-pressed={!!c.completedAt} onClick={() => store.setCompleted(c.id, !c.completedAt).catch(failed)}>
    <IconCheck />
    Feito
  </button>
);
